import { useEffect, useRef, useCallback, useState } from "react";
import "./LiquidityRadar.css";

const WS_URL = "wss://base.akscryptodada.com/ws/radar";

// ── Helpers ──────────────────────────────────────────────────
const fmt  = (n, d = 0) => (n == null || isNaN(n)) ? "—" : Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtM = (n) => {
  if (n == null || isNaN(n)) return "—";
  if (n >= 1e9) return "$" + (n / 1e9).toFixed(2) + "B";
  if (n >= 1e6) return "$" + (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return "$" + (n / 1e3).toFixed(1) + "K";
  if (n > 0)    return "$" + n.toFixed(0);
  return "—";
};
const ago = (ms) => {
  const s = Math.floor(ms / 1000);
  if (s < 60) return s + "s ago";
  if (s < 3600) return Math.floor(s / 60) + "m ago";
  return Math.floor(s / 3600) + "h ago";
};
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

function drawCVD(canvas, w, h, history) {
  if (!canvas) return;
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, w, h);
  const valid = (history || []).filter(x => typeof x.cvd === "number" && !isNaN(x.cvd));
  if (valid.length < 1) {
    ctx.strokeStyle = "rgba(255,255,255,0.04)"; ctx.lineWidth = 0.5; ctx.setLineDash([3, 3]);
    ctx.beginPath(); ctx.moveTo(0, h / 2); ctx.lineTo(w, h / 2); ctx.stroke(); ctx.setLineDash([]);
    ctx.fillStyle = "rgba(100,110,130,0.5)"; ctx.font = "9px monospace";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText("Waiting for trade data...", w / 2, h / 2);
    return;
  }
  const data = valid.slice(-150).map(x => x.cvd);
  const min = Math.min(...data), max = Math.max(...data);
  const rng = Math.max(max - min, 0.0001);
  const nx = i => data.length > 1 ? (i / (data.length - 1)) * w : w / 2;
  const ny = v => h - ((v - min) / rng) * (h - 12) - 6;
  const zy = ny(0);
  if (zy > 2 && zy < h - 2) {
    ctx.beginPath(); ctx.moveTo(0, zy); ctx.lineTo(w, zy);
    ctx.strokeStyle = "rgba(255,255,255,0.1)"; ctx.lineWidth = 0.5;
    ctx.setLineDash([3, 3]); ctx.stroke(); ctx.setLineDash([]);
  }
  const lastVal = data[data.length - 1];
  const bullish = lastVal >= (data[0] || 0);
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  if (bullish) { grad.addColorStop(0, "rgba(0,208,132,0.25)"); grad.addColorStop(1, "rgba(0,208,132,0.02)"); }
  else { grad.addColorStop(0, "rgba(255,77,109,0.02)"); grad.addColorStop(1, "rgba(255,77,109,0.25)"); }
  ctx.beginPath();
  data.forEach((v, i) => i === 0 ? ctx.moveTo(nx(i), ny(v)) : ctx.lineTo(nx(i), ny(v)));
  ctx.strokeStyle = bullish ? "#00d084" : "#ff4d6d"; ctx.lineWidth = 1.5; ctx.stroke();
  if (data.length > 1) { ctx.lineTo(w, h); ctx.lineTo(0, h); ctx.closePath(); ctx.fillStyle = grad; ctx.fill(); }
  ctx.beginPath(); ctx.arc(data.length > 1 ? w - 3 : w / 2, ny(lastVal), 3, 0, Math.PI * 2);
  ctx.fillStyle = bullish ? "#00d084" : "#ff4d6d"; ctx.fill();
}

function drawRadar(canvas, d) {
  if (!canvas || !d) return;
  const size = 280;
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext("2d");
  const cx = size / 2, cy = size / 2, r = 110;
  ctx.clearRect(0, 0, size, size);
  const sc = d.scores || {};

  // Grid rings
  [0.33, 0.66, 1].forEach(f => {
    ctx.beginPath(); ctx.arc(cx, cy, r * f, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(255,255,255,0.05)"; ctx.lineWidth = 1; ctx.stroke();
  });

  const axes = [
    { label: "BIAS",    val: (sc.biasScore    || 50) / 100 },
    { label: "STRENGTH",val: (sc.marketStrength|| 50) / 100 },
    { label: "MAGNET",  val: clamp((sc.magnetStrength || 0) / 100, 0, 1) },
    { label: "SWEEP",   val: clamp((sc.sweepConf      || 0) / 100, 0, 1) },
    { label: "SPOOF",   val: clamp((sc.spoofProb      || 0) / 100, 0, 1) },
    { label: "OI",      val: clamp(Math.abs(sc.oiChange || 0) / 5, 0, 1) },
  ];

  // Axis lines
  axes.forEach((_, i) => {
    const angle = (i / axes.length) * Math.PI * 2 - Math.PI / 2;
    ctx.beginPath(); ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(angle) * r, cy + Math.sin(angle) * r);
    ctx.strokeStyle = "rgba(255,255,255,0.07)"; ctx.lineWidth = 1; ctx.stroke();
  });

  // Fill polygon
  ctx.beginPath();
  axes.forEach((ax, i) => {
    const angle = (i / axes.length) * Math.PI * 2 - Math.PI / 2;
    const x = cx + Math.cos(angle) * r * ax.val;
    const y = cy + Math.sin(angle) * r * ax.val;
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  });
  ctx.closePath();
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
  grad.addColorStop(0, "rgba(6,214,214,0.35)");
  grad.addColorStop(1, "rgba(59,130,246,0.08)");
  ctx.fillStyle = grad; ctx.fill();
  ctx.strokeStyle = "#06d6d6"; ctx.lineWidth = 1.5; ctx.stroke();

  // Dots + labels
  axes.forEach((ax, i) => {
    const angle = (i / axes.length) * Math.PI * 2 - Math.PI / 2;
    const x = cx + Math.cos(angle) * r * ax.val;
    const y = cy + Math.sin(angle) * r * ax.val;
    ctx.beginPath(); ctx.arc(x, y, 3.5, 0, Math.PI * 2);
    ctx.fillStyle = "#06d6d6"; ctx.fill();
    const lx = cx + Math.cos(angle) * (r + 18);
    const ly = cy + Math.sin(angle) * (r + 18);
    ctx.fillStyle = "rgba(123,133,160,0.9)";
    ctx.font = "bold 8px 'JetBrains Mono', monospace";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(ax.label, lx, ly);
  });
}

// ── Main Component ────────────────────────────────────────────
export default function LiquidityRadar() {
  const [activeTab, setActiveTab] = useState("main");
  const [connState, setConnState] = useState("CONNECTING");
  const [data, setData]           = useState(null);
  const [alerts, setAlerts]       = useState([]);
  const [alertCount, setAlertCount] = useState(0);
  const [clock, setClock]         = useState("");
  const [expandedExp, setExpandedExp] = useState({});
  const [alertSettings, setAlertSettings] = useState({ sweep: true, spoof: true, target: true, squeeze: true });

  const wsRef        = useRef(null);
  const basePriceRef = useRef(0);
  const reconnectMs  = useRef(1000);
  const radarCanvasRef = useRef(null);
  const cvdMiniRef     = useRef(null);
  const cvdFullRef     = useRef(null);

  // Clock
  useEffect(() => {
    const t = setInterval(() => setClock(new Date().toUTCString().slice(17, 25) + " UTC"), 1000);
    return () => clearInterval(t);
  }, []);

  // WebSocket
  const connect = useCallback(() => {
    try { wsRef.current?.close(); } catch (_) {}
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => { setConnState("LIVE"); reconnectMs.current = 1000; };
    ws.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }
      if (msg.type === "update") {
        if (!basePriceRef.current && msg.data?.price > 0) basePriceRef.current = msg.data.price;
        setData(msg.data);
      } else if (msg.type === "alert") {
        setAlerts(prev => [msg.data, ...prev].slice(0, 100));
        setAlertCount(c => c + 1);
      } else if (msg.type === "status") {
        setConnState(msg.data?.status === "LIVE" ? "LIVE" : "WARN");
      }
    };
    ws.onclose = () => {
      setConnState("RECONNECTING");
      setTimeout(connect, reconnectMs.current);
      reconnectMs.current = Math.min(reconnectMs.current * 2, 15000);
    };
    ws.onerror = () => setConnState("ERROR");
  }, []);

  useEffect(() => { connect(); return () => { try { wsRef.current?.close(); } catch (_) {} }; }, [connect]);

  // Draw canvases when data changes
  useEffect(() => {
    if (!data) return;
    if (radarCanvasRef.current) drawRadar(radarCanvasRef.current, data);
    if (cvdMiniRef.current && data.cvd?.history?.length) drawCVD(cvdMiniRef.current, 280, 65, data.cvd.history);
    if (cvdFullRef.current && data.cvd?.history?.length && activeTab === "advanced") drawCVD(cvdFullRef.current, 440, 120, data.cvd.history);
  }, [data, activeTab]);

  const toggleExp = (key) => setExpandedExp(prev => ({ ...prev, [key]: !prev[key] }));

  const sc = data?.scores  || {};
  const cv = data?.cvd     || {};
  const p  = data?.price   || data?.markPrice || 0;
  const basePrice = basePriceRef.current || p;
  const pct = basePrice > 0 ? ((p - basePrice) / basePrice * 100) : 0;
  const bs  = sc.biasScore != null ? sc.biasScore : 50;
  const str = sc.marketStrength || 50;
  const fr  = data?.fundingRate;
  const sp  = sc.spoofProb || 0;
  const oiC = sc.oiChange  || 0;

  const connDotClass = connState === "LIVE" ? "lr-conn-dot live" : connState.includes("ERROR") ? "lr-conn-dot error" : "lr-conn-dot";

  return (
    <div className="lr-root">
      <div className="lr-container">

      {/* TOP BAR */}
      <header className="lr-topbar">
        <div className="lr-logo">
          <div className="lr-logo-ico">₿</div>
          <div>
            <div className="lr-logo-text">LIQUIDITY RADAR</div>
            <div className="lr-logo-sub">BTCUSDT PERP · BINANCE FUTURES</div>
          </div>
        </div>

        <nav className="lr-tabs">
          {[["main","RADAR"],["advanced","ADVANCED"],["events","ORDER EVENTS"],["alerts","ALERTS"]].map(([key, label]) => (
            <button key={key} className={`lr-tab${activeTab === key ? " active" : ""}`} onClick={() => setActiveTab(key)}>
              {label}{key === "alerts" && <span className="lr-badge">{alertCount}</span>}
            </button>
          ))}
        </nav>

        <div className="lr-statusbar">
          <div className="lr-conn-pill">
            <span className={connDotClass}></span>
            <span>{connState}</span>
          </div>
          <span>OI <strong>{fmtM(data?.openInterest)}</strong></span>
          <span>FR <strong style={{ color: fr > 0 ? "var(--green)" : fr < 0 ? "var(--red)" : undefined }}>{fr != null ? (fr >= 0 ? "+" : "") + (fr * 100).toFixed(4) + "%" : "—"}</strong></span>
          <span>SPR <strong>{data?.book?.spread > 0 ? "$" + data.book.spread.toFixed(1) : "—"}</strong></span>
          <span>TRD <strong style={{ color: "var(--cyan)" }}>{data?.tradeCount || 0}</strong></span>
          <span>{clock}</span>
        </div>
      </header>

      <main className="lr-main">
      <div className="lr-inner">

        {/* ══════════ RADAR TAB ══════════ */}
        <section className={`lr-tab-content${activeTab === "main" ? " active" : ""}`}>
          <div className="lr-grid-main">
            <div className="lr-col-left">

              {/* Price Card */}
              <div className="lr-card">
                <div className="lr-card-title">
                  <span className="lr-dot lr-dot-green lr-pulse"></span>
                  BTCUSDT PERPETUAL
                  <span className={`lr-chip ${bs >= 58 ? "lr-chip-up" : bs >= 44 ? "lr-chip-neutral" : "lr-chip-down"}`}>
                    {bs >= 70 ? "STRONG BULLISH" : bs >= 58 ? "BULLISH" : bs >= 44 ? "NEUTRAL" : bs >= 30 ? "BEARISH" : "STRONG BEARISH"}
                  </span>
                  <span className="lr-spacer"></span>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                    <span className="lr-score-huge lr-score-gradient">{sc.overallConf > 0 ? Math.round(sc.overallConf) : "—"}</span>
                    <div style={{ paddingBottom: 6, lineHeight: 1.3 }}>
                      <div style={{ fontSize: 9, color: "var(--t3)", letterSpacing: ".1em", fontFamily: "var(--font-mono)" }}>CONFIDENCE</div>
                      <div style={{ fontSize: 9, color: "var(--t2)", fontFamily: "var(--font-mono)" }}>/ 100</div>
                    </div>
                  </div>
                </div>
                <div className="lr-price-row">
                  <span className="lr-price-main">{p > 0 ? "$" + fmt(p) : "—"}</span>
                  <span className={`lr-price-change ${pct >= 0 ? "lr-up" : "lr-down"}`}>{p > 0 ? (pct >= 0 ? "+" : "") + pct.toFixed(2) + "%" : "—"}</span>
                </div>
                <div className="lr-price-meta">
                  <span>24H HIGH <strong>{data?.high24 > 0 ? "$" + fmt(data.high24) : "—"}</strong></span>
                  <span>24H LOW <strong>{data?.low24 > 0 ? "$" + fmt(data.low24) : "—"}</strong></span>
                  <span>VOLUME <strong>{fmtM(data?.vol24)}</strong></span>
                  <span>MARK <strong>{data?.markPrice > 0 ? "$" + fmt(data.markPrice) : "—"}</strong></span>
                </div>
                <div className="lr-sep"></div>
                <div className="lr-field-label">MARKET BIAS <button className="lr-why" onClick={() => toggleExp("biasExp")}>?</button></div>
                <div className="lr-bias-track"><div className="lr-bias-fill" style={{ width: bs + "%" }}></div></div>
                <div className="lr-bias-labels">
                  <span className="lr-green lr-fw">{bs.toFixed(1)}% BUY</span>
                  <span style={{ color: "var(--t2)" }}>{bs.toFixed(1)}/100</span>
                  <span className="lr-red lr-fw">{(100 - bs).toFixed(1)}% SELL</span>
                </div>
                {expandedExp.biasExp && <div className="lr-explain open">Market bias is calculated from live trade flow (aggressive buys vs sells), order book imbalance within 0.2% of price, and CVD slope. A score above 60 means buy pressure dominates.</div>}
              </div>

              {/* 3 metric cards */}
              <div className="lr-grid-3">
                <div className="lr-card">
                  <div className="lr-card-title"><span className={`lr-dot ${(sc.magnetStrength||0) > 60 ? "lr-dot-green" : (sc.magnetStrength||0) > 30 ? "lr-dot-amber" : "lr-dot-red"}`}></span>LIQUIDITY MAGNET</div>
                  <div className="lr-metric-price lr-cyan">{sc.magnetPrice > 0 ? "$" + fmt(sc.magnetPrice) : "—"}</div>
                  <div className="lr-metric-desc">Largest stop order and liquidation cluster. Price is pulled toward this level.</div>
                  <div className="lr-sep"></div>
                  {sc.magnetPrice > 0 && p > 0 && (
                    <>
                      <div className="lr-kv"><span>Distance</span><span className={`lr-fw ${((sc.magnetPrice - p) / p * 100) >= 0 ? "lr-green" : "lr-red"}`}>{(((sc.magnetPrice - p) / p * 100) >= 0 ? "+" : "") + ((sc.magnetPrice - p) / p * 100).toFixed(2) + "%"}</span></div>
                      <div className="lr-kv"><span>Cluster $</span><span className="lr-fw">{fmtM((sc.magnetStrength || 0) * 50000)}</span></div>
                    </>
                  )}
                </div>

                <div className="lr-card">
                  <div className="lr-card-title"><span className="lr-dot lr-dot-amber lr-pulse"></span>LIKELY TARGET</div>
                  <div className="lr-metric-price lr-amber">{sc.targetPrice > 0 ? "$" + fmt(sc.targetPrice) : "—"}</div>
                  <div className="lr-metric-desc">Highest-probability level based on OB density, OI clusters, and CVD direction.</div>
                  <div className="lr-sep"></div>
                  <div className="lr-kv"><span>Score</span><span className="lr-chip lr-chip-warn">{sc.targetScore != null ? sc.targetScore.toFixed(0) + "/100" : "—"}</span></div>
                  <div className="lr-kv"><span>Type</span><span className="lr-amber lr-fw">{sc.targetType || "—"}</span></div>
                </div>

                <div className="lr-card" style={{ textAlign: "center" }}>
                  <div className="lr-card-title" style={{ justifyContent: "center" }}>MARKET STRENGTH</div>
                  <div style={{ display: "flex", justifyContent: "center", margin: "6px 0 8px" }}>
                    <div className="lr-ring-wrap">
                      <svg width="88" height="88" viewBox="0 0 88 88">
                        <circle cx="44" cy="44" r="36" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8"/>
                        <circle cx="44" cy="44" r="36" fill="none" stroke="var(--cyan)" strokeWidth="8"
                          strokeDasharray="226" strokeDashoffset={(226 - str / 100 * 226).toFixed(1)}
                          strokeLinecap="round"
                          style={{ transform: "rotate(-90deg)", transformOrigin: "center", filter: "drop-shadow(0 0 6px var(--cyan))", transition: "stroke-dashoffset .6s" }}/>
                      </svg>
                      <div className="lr-ring-inner">
                        <span className="lr-ring-num lr-cyan">{str.toFixed(0)}</span>
                        <span className="lr-ring-lbl" style={{ color: str >= 70 ? "var(--cyan)" : str >= 45 ? "var(--amber)" : "var(--red)" }}>{str >= 70 ? "STRONG" : str >= 45 ? "MODERATE" : "WEAK"}</span>
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: "var(--t3)", fontFamily: "var(--font-mono)" }}>{str >= 70 ? "Trend supported by volume and OI." : str >= 45 ? "Mixed signals — monitor closely." : "Weak trend with low conviction."}</div>
                </div>
              </div>

              {/* Sweep + Traps */}
              <div className="lr-grid-2">
                <div className="lr-card">
                  <div className="lr-card-title">LAST LIQUIDITY SWEEP <button className="lr-why" onClick={() => toggleExp("sweepExp")}>?</button></div>
                  {(() => {
                    const sw = sc.sweepType;
                    const hasSweep = sw && sw !== "none" && sc.sweepPrice > 0 && sc.sweepConf >= 30;
                    const isBull = sw?.includes("bull");
                    const isStrong = sw?.includes("strong");
                    return (
                      <>
                        <div className={`lr-sweep-badge ${hasSweep ? (isBull ? "lr-sweep-bull" : "lr-sweep-bear") : "lr-sweep-none"}`}>
                          {hasSweep ? (isStrong ? "🔥 STRONG " : "〰 WEAK ") + (isBull ? "BULLISH SWEEP" : "BEARISH SWEEP") : "NO SWEEP DETECTED"}
                        </div>
                        <div style={{ fontSize: 10, color: "var(--t2)", lineHeight: 1.7 }}>
                          {hasSweep ? `Price swept $${fmt(sc.sweepPrice)}. ${isStrong ? "Strong rejection detected." : "Moderate recovery."}` : "Monitoring live price for sweep events..."}
                        </div>
                        {hasSweep && (
                          <>
                            <div className="lr-sep"></div>
                            <div style={{ display: "flex", gap: 18, fontSize: 10 }}>
                              <div><div style={{ color: "var(--t3)", fontFamily: "var(--font-mono)", fontSize: 9, marginBottom: 3 }}>AT PRICE</div><div style={{ fontWeight: 600, fontFamily: "var(--font-mono)" }}>${fmt(sc.sweepPrice)}</div></div>
                              <div><div style={{ color: "var(--t3)", fontFamily: "var(--font-mono)", fontSize: 9, marginBottom: 3 }}>CONFIDENCE</div><div style={{ fontWeight: 700, fontFamily: "var(--font-mono)", color: sc.sweepConf >= 65 ? "var(--green)" : "var(--amber)" }}>{(sc.sweepConf || 0).toFixed(0)}/100</div></div>
                              <div><div style={{ color: "var(--t3)", fontFamily: "var(--font-mono)", fontSize: 9, marginBottom: 3 }}>AGE</div><div style={{ fontWeight: 600, fontFamily: "var(--font-mono)" }}>{sc.sweepAge ? ago(Date.now() - sc.sweepAge) : "—"}</div></div>
                            </div>
                          </>
                        )}
                      </>
                    );
                  })()}
                  {expandedExp.sweepExp && <div className="lr-explain open">A liquidity sweep occurs when price breaks a previous high or low, triggers clustered stop-loss orders and liquidations, then quickly reverses.</div>}
                </div>

                <div className="lr-card">
                  <div className="lr-card-title">TRAP &amp; SQUEEZE RISK <button className="lr-why" onClick={() => toggleExp("trapExp")}>?</button></div>
                  {[
                    ["Bull Trap",    sc.bullTrapRisk    || 0, "var(--red)"],
                    ["Bear Trap",    sc.bearTrapRisk    || 0, "var(--amber)"],
                    ["Short Squeeze",sc.shortSqueezeRisk|| 0, "var(--green)"],
                    ["Long Squeeze", sc.longSqueezeRisk || 0, "var(--blue)"],
                  ].map(([label, val, col]) => (
                    <div key={label} className="lr-risk-row">
                      <span className="lr-risk-lbl">{label}</span>
                      <div className="lr-risk-track"><div className="lr-risk-fill" style={{ width: clamp(val, 0, 100) + "%", background: col }}></div></div>
                      <span className="lr-risk-val" style={{ color: val >= 70 ? col : "var(--t2)" }}>{Math.round(val)}</span>
                    </div>
                  ))}
                  {expandedExp.trapExp && <div className="lr-explain open">Bull Trap = price up but CVD not confirming. Bear Trap = price down but CVD showing hidden buying. Squeeze = forced position closures.</div>}
                </div>
              </div>

              {/* Target Zones */}
              <div className="lr-card">
                <div className="lr-card-title">LIQUIDITY TARGET ZONES <button className="lr-why" onClick={() => toggleExp("targetExp")}>?</button></div>
                <div style={{ fontSize: 10, color: "var(--t2)", marginBottom: 10, lineHeight: 1.6 }}>Largest order clusters from live order book — price gravitates toward these levels.</div>
                {(() => {
                  const clusters = data?.book?.clusters || [];
                  if (!clusters.length) return <div style={{ color: "var(--t3)", fontSize: 10 }}>Loading order book data...</div>;
                  const maxVal = clusters[0]?.value || 1;
                  return clusters.slice(0, 6).map((c, i) => {
                    const above = c.price >= p;
                    const col = above ? "var(--amber)" : "var(--blue)";
                    const pct2 = (c.value / maxVal * 100).toFixed(0);
                    const score = Math.min(Math.round(c.value / 800000 * 100), 99);
                    return (
                      <div key={i} className="lr-trow">
                        <span className="lr-tprice" style={{ color: col }}>{above ? "▲" : "▼"} ${fmt(c.price)}</span>
                        <div className="lr-tbar-wrap"><div className="lr-tbar" style={{ width: pct2 + "%", background: col + "22", color: col }}>{above ? "Sell Wall" : "Buy Wall"} · {fmtM(c.value)}</div></div>
                        <span className="lr-tscore" style={{ color: col }}>{score}/100</span>
                      </div>
                    );
                  });
                })()}
                {expandedExp.targetExp && <div className="lr-explain open">These zones are built from real-time order book data. Each bar represents a $200-wide price bucket with the total USD value of resting orders.</div>}
              </div>

            </div>

            {/* RIGHT COLUMN */}
            <div className="lr-col-right">

              {/* Radar Canvas */}
              <div className="lr-card">
                <div className="lr-card-title"><span className="lr-dot lr-dot-green lr-pulse"></span>LIVE RADAR</div>
                <canvas ref={radarCanvasRef} width="280" height="280" style={{ display: "block", margin: "0 auto" }}></canvas>
              </div>

              {/* Spoofing */}
              <div className="lr-card">
                <div className="lr-card-title"><span className={`lr-dot${sp >= 70 ? " lr-dot-red lr-pulse" : sp >= 50 ? " lr-dot-amber lr-pulse" : ""}`}></span>POSSIBLE SPOOFING <button className="lr-why" onClick={() => toggleExp("spoofExp")}>?</button></div>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 34, fontWeight: 800, fontFamily: "var(--font-mono)", lineHeight: 1, color: sp >= 70 ? "var(--red)" : sp >= 50 ? "var(--amber)" : "var(--t2)" }}>{sp.toFixed(0)}/100</div>
                    <div style={{ fontSize: 9, color: "var(--t3)", fontFamily: "var(--font-mono)", letterSpacing: ".08em", marginTop: 2 }}>PROBABILITY</div>
                  </div>
                  <div style={{ fontSize: 10, color: "var(--t2)", lineHeight: 1.7, flex: 1 }}>{sc.spoofDetail || "Monitoring live order book for suspicious wall behavior..."}</div>
                </div>
                <div className="lr-spoof-meter">
                  {([...(sc.spoofMeter || [])].length ? [...(sc.spoofMeter || [])].slice(0, 20).reverse() : Array(16).fill(0)).map((v, i) => (
                    <div key={i} className="lr-spoof-bar" style={{ height: Math.max(3, (v / 100) * 38) + "px", background: v >= 70 ? "#ff4d6d" : v >= 50 ? "#f5a623" : "rgba(255,255,255,0.06)" }}></div>
                  ))}
                </div>
                <div style={{ fontSize: 9, color: "var(--t3)", fontFamily: "var(--font-mono)", padding: "5px 0", borderTop: "1px solid var(--border)" }}>⚠ Probability estimate only — not financial advice.</div>
                {expandedExp.spoofExp && <div className="lr-explain open">Score factors: cancellation speed, proximity to price, order size, fill percentage, and repeat behavior at same price.</div>}
              </div>

              {/* Funding + OI */}
              <div className="lr-card">
                <div className="lr-card-title">FUNDING RATE + OPEN INTEREST</div>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 26, fontWeight: 700, fontFamily: "var(--font-mono)", lineHeight: 1, color: fr > 0 ? "var(--green)" : fr < 0 ? "var(--red)" : undefined }}>{fr != null ? (fr >= 0 ? "+" : "") + (fr * 100).toFixed(4) + "%" : "—"}</div>
                    <div style={{ fontSize: 9, color: "var(--t3)", fontFamily: "var(--font-mono)", letterSpacing: ".08em", marginTop: 3 }}>8H RATE</div>
                  </div>
                  <div style={{ fontSize: 10, color: "var(--t2)", lineHeight: 1.7, flex: 1 }}>
                    {fr == null ? "—" : fr > 0.001 ? "Very positive — longs paying shorts heavily. Overheated." : fr > 0.0002 ? "Positive — longs pay shorts. Normal bullish lean." : fr < -0.001 ? "Highly negative — shorts paying longs. Short squeeze risk." : fr < -0.0002 ? "Negative — shorts pay longs. Bearish lean." : "Neutral funding rate. No extreme lean."}
                  </div>
                </div>
                <div className="lr-sep"></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  <div className="lr-mini-stat"><div className="lr-mv lr-cyan">{fmtM(data?.openInterest)}</div><div className="lr-ml">OPEN INT.</div></div>
                  <div className="lr-mini-stat"><div className="lr-mv" style={{ color: oiC > 0 ? "var(--green)" : oiC < 0 ? "var(--red)" : undefined }}>{(oiC >= 0 ? "+" : "") + oiC.toFixed(2) + "%"}</div><div className="lr-ml">OI CHANGE</div></div>
                  <div className="lr-mini-stat"><div className="lr-mv" style={{ color: (sc.nextSweepProb || 0) >= 70 ? "var(--amber)" : undefined }}>{Math.round(sc.nextSweepProb || 0)}%</div><div className="lr-ml">NEXT SWEEP</div></div>
                </div>
              </div>

              {/* CVD */}
              <div className="lr-card">
                <div className="lr-card-title">CVD — VOLUME DELTA <button className="lr-why" onClick={() => toggleExp("cvdExp")}>?</button></div>
                <canvas ref={cvdMiniRef} width="276" height="68" style={{ display: "block", width: "100%" }}></canvas>
                {expandedExp.cvdExp && <div className="lr-explain open">CVD = aggressive buys minus aggressive sells, cumulated. Rising CVD = more money entering via market buys.</div>}
                <div className="lr-sep"></div>
                <div style={{ display: "flex", justifyContent: "space-around", fontSize: 10 }}>
                  <div style={{ textAlign: "center" }}><div className="lr-green lr-fw" style={{ fontSize: 13, fontFamily: "var(--font-mono)" }}>{(cv.sessionBuy || 0).toFixed(2)} BTC</div><div style={{ color: "var(--t3)", fontFamily: "var(--font-mono)", fontSize: 9, marginTop: 2 }}>BUY VOL</div></div>
                  <div style={{ textAlign: "center" }}><div className="lr-red lr-fw" style={{ fontSize: 13, fontFamily: "var(--font-mono)" }}>{(cv.sessionSell || 0).toFixed(2)} BTC</div><div style={{ color: "var(--t3)", fontFamily: "var(--font-mono)", fontSize: 9, marginTop: 2 }}>SELL VOL</div></div>
                  <div style={{ textAlign: "center" }}><div style={{ fontSize: 13, fontFamily: "var(--font-mono)", fontWeight: 600, color: (cv.sessionDelta || 0) > 0 ? "var(--green)" : (cv.sessionDelta || 0) < 0 ? "var(--red)" : "var(--cyan)" }}>{((cv.sessionDelta || 0) >= 0 ? "+" : "") + (cv.sessionDelta || 0).toFixed(2)} BTC</div><div style={{ color: "var(--t3)", fontFamily: "var(--font-mono)", fontSize: 9, marginTop: 2 }}>DELTA</div></div>
                  <div style={{ textAlign: "center" }}><div style={{ fontSize: 13, fontFamily: "var(--font-mono)", fontWeight: 600, color: cv.trend === "bullish" ? "var(--green)" : cv.trend === "bearish" ? "var(--red)" : "var(--t2)" }}>{cv.trend === "bullish" ? "Bullish ↑" : cv.trend === "bearish" ? "Bearish ↓" : "Flat —"}</div><div style={{ color: "var(--t3)", fontFamily: "var(--font-mono)", fontSize: 9, marginTop: 2 }}>TREND</div></div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ══════════ ADVANCED TAB ══════════ */}
        <section className={`lr-tab-content${activeTab === "advanced" ? " active" : ""}`}>
          <div className="lr-adv-grid">
            {/* Order Book */}
            <div className="lr-card lr-full" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              <div>
                <div className="lr-card-title">ORDER BOOK — ASKS (SELL WALLS)</div>
                <div className="lr-ob-header"><span>Price</span><span>Size (BTC)</span><span style={{ textAlign: "right" }}>Depth ($)</span></div>
                <div style={{ maxHeight: 240, overflowY: "auto" }}>
                  {(data?.book?.asks || []).slice(0, 15).map((row, i) => (
                    <div key={i} className="lr-ob-row lr-ob-ask">
                      <div className="lr-ob-depth" style={{ width: Math.min((row[1] / 5) * 100, 100) + "%" }}></div>
                      <span className="lr-ob-price">{fmt(row[0])}</span>
                      <span className="lr-ob-size">{row[1]?.toFixed(3)}</span>
                      <span className="lr-ob-total" style={{ textAlign: "right" }}>{fmtM(row[0] * row[1])}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="lr-card-title">ORDER BOOK — BIDS (BUY WALLS)</div>
                <div className="lr-ob-header"><span>Price</span><span>Size (BTC)</span><span style={{ textAlign: "right" }}>Depth ($)</span></div>
                <div style={{ maxHeight: 240, overflowY: "auto" }}>
                  {(data?.book?.bids || []).slice(0, 15).map((row, i) => (
                    <div key={i} className="lr-ob-row lr-ob-bid">
                      <div className="lr-ob-depth" style={{ width: Math.min((row[1] / 5) * 100, 100) + "%" }}></div>
                      <span className="lr-ob-price">{fmt(row[0])}</span>
                      <span className="lr-ob-size">{row[1]?.toFixed(3)}</span>
                      <span className="lr-ob-total" style={{ textAlign: "right" }}>{fmtM(row[0] * row[1])}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Liquidation Map */}
            <div className="lr-card">
              <div className="lr-card-title">LIQUIDATION MAP</div>
              <div style={{ fontSize: 10, color: "var(--t2)", marginBottom: 10, lineHeight: 1.6 }}>Estimated levels where leveraged positions get force-closed.</div>
              {(data?.liqMap || []).slice(0, 10).map((liq, i) => {
                const isLong = liq.side === "long";
                const col = isLong ? "var(--green)" : "var(--red)";
                return (
                  <div key={i} className="lr-lrow">
                    <span className="lr-lside" style={{ color: col }}>{isLong ? "LONG" : "SHORT"}</span>
                    <div className="lr-lbar-wrap"><div className="lr-lbar" style={{ width: Math.min((liq.value / 1e7) * 100, 100) + "%", background: col + "22", color: col }}>{fmtM(liq.value)}</div></div>
                    <span className="lr-lprice">${fmt(liq.price)}</span>
                  </div>
                );
              })}
              {!(data?.liqMap?.length) && <div style={{ color: "var(--t3)", fontSize: 10 }}>Loading liquidation data...</div>}
            </div>

            {/* CVD Full */}
            <div className="lr-card">
              <div className="lr-card-title">CVD — EXTENDED SESSION VIEW</div>
              <canvas ref={cvdFullRef} width="440" height="120" style={{ display: "block", width: "100%" }}></canvas>
            </div>

            {/* Volume Profile */}
            <div className="lr-card">
              <div className="lr-card-title">VOLUME PROFILE — SESSION</div>
              <div style={{ fontSize: 10, color: "var(--t2)", marginBottom: 8 }}>★ = Point of Control (most traded level)</div>
              {(() => {
                const vp = data?.volumeProfile || [];
                const maxVol = Math.max(...vp.map(v => v.vol || 0), 1);
                return vp.slice(0, 14).map((v, i) => (
                  <div key={i} className="lr-vprow">
                    <span className="lr-vpp">{v.poc ? "★ " : ""}{fmt(v.price)}</span>
                    <div className="lr-vpbw"><div className="lr-vpb" style={{ width: (v.vol / maxVol * 100) + "%", background: v.poc ? "var(--amber)" : "var(--blue)" }}></div></div>
                    <span className="lr-vpv">{fmtM(v.vol)}</span>
                  </div>
                ));
              })()}
              {!(data?.volumeProfile?.length) && <div style={{ color: "var(--t3)", fontSize: 10 }}>Loading volume data...</div>}
            </div>

            {/* Active Walls */}
            <div className="lr-card lr-full">
              <div className="lr-card-title">ACTIVE WALLS — LARGE RESTING ORDERS</div>
              <div style={{ fontSize: 10, color: "var(--t2)", marginBottom: 10 }}>Orders ≥ 5 BTC within 2% of current price, sorted by size.</div>
              <div className="lr-wall-row header"><span>PRICE</span><span>SIDE</span><span>SIZE (BTC)</span><span>VALUE ($)</span><span>DISTANCE</span></div>
              {(data?.book?.walls || []).slice(0, 12).map((w, i) => (
                <div key={i} className="lr-wall-row">
                  <span style={{ color: w.side === "ask" ? "var(--red)" : "var(--green)" }}>${fmt(w.price)}</span>
                  <span style={{ color: w.side === "ask" ? "var(--red)" : "var(--green)" }}>{w.side === "ask" ? "SELL" : "BUY"}</span>
                  <span>{(w.qty || 0).toFixed(2)}</span>
                  <span>{fmtM(w.usd || w.price * w.qty)}</span>
                  <span style={{ color: "var(--t2)" }}>{p > 0 ? (((w.price - p) / p * 100) >= 0 ? "+" : "") + ((w.price - p) / p * 100).toFixed(2) + "%" : "—"}</span>
                </div>
              ))}
              {!(data?.book?.walls?.length) && <div style={{ color: "var(--t3)", fontSize: 10, padding: "8px 0" }}>Loading wall data...</div>}
            </div>
          </div>
        </section>

        {/* ══════════ ORDER EVENTS TAB ══════════ */}
        <section className={`lr-tab-content${activeTab === "events" ? " active" : ""}`}>
          {(() => {
            // Server sends events inside d.book.events[]
            const events = data?.book?.events || [];
            // Spoof candidates: APPEAR type, qty >= 5, seen in last 90s
            const spoofCandidates = events.filter(e =>
              e.type === "APPEAR" && (e.qty || 0) >= 5 && (Date.now() - (e.ts || 0)) < 90000
            );
            const typeMap = { APPEAR: "lr-ev-appear", CANCEL: "lr-ev-cancel", FILL: "lr-ev-fill", PARTIAL: "lr-ev-partial" };
            return (
              <div className="lr-grid-2">
                <div className="lr-card">
                  <div className="lr-card-title">LARGE ORDER EVENTS — LIVE LOG</div>
                  <div style={{ fontSize: 10, color: "var(--t2)", marginBottom: 10 }}>Every order ≥ 2 BTC that appears, fills, moves, or cancels.</div>
                  <div style={{ maxHeight: 520, overflowY: "auto" }}>
                    {events.length > 0
                      ? events.slice(0, 80).map((ev, i) => (
                          <div key={i} className="lr-ev-row">
                            <span className={`lr-ev-type ${typeMap[ev.type] || "lr-ev-appear"}`}>{(ev.type || "APPEAR")}</span>
                            <span style={{ color: ev.side === "bid" ? "var(--green)" : "var(--red)", width: 34 }}>{ev.side === "bid" ? "BUY" : "SELL"}</span>
                            <span style={{ flex: 1, color: "var(--text)", fontFamily: "var(--font-mono)" }}>${fmt(ev.price)}</span>
                            <span style={{ color: "var(--t2)" }}>{(ev.qty || 0).toFixed(3)} BTC</span>
                            <span style={{ color: "var(--t3)", marginLeft: 6 }}>{ev.ts ? ago(Date.now() - ev.ts) : "—"}</span>
                          </div>
                        ))
                      : <div style={{ color: "var(--t3)", textAlign: "center", padding: 28, fontSize: 11, fontFamily: "var(--font-mono)" }}>Watching for large orders (≥ 2 BTC)...</div>
                    }
                  </div>
                </div>
                <div className="lr-card">
                  <div className="lr-card-title">ACTIVE SPOOF WATCH — MONITORING</div>
                  <div style={{ fontSize: 10, color: "var(--t2)", marginBottom: 10 }}>Large orders (≥ 5 BTC) being watched for suspicious behavior.</div>
                  <div style={{ maxHeight: 520, overflowY: "auto" }}>
                    {spoofCandidates.length > 0
                      ? spoofCandidates.slice(0, 15).map((e, i) => {
                          const age = Date.now() - (e.ts || Date.now());
                          const col = e.side === "ask" ? "var(--red)" : "var(--green)";
                          return (
                            <div key={i} className={`lr-sw-card ${(e.qty || 0) >= 15 ? "vhigh" : (e.qty || 0) >= 8 ? "high" : ""}`}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                                <span style={{ color: col, fontWeight: 600, fontFamily: "var(--font-mono)", fontSize: 11 }}>${fmt(e.price)}</span>
                                <span style={{ color: "var(--t2)", fontFamily: "var(--font-mono)", fontSize: 11 }}>{(e.qty || 0).toFixed(2)} BTC · {e.side === "ask" ? "SELL" : "BUY"}</span>
                              </div>
                              <div style={{ fontSize: 10, color: "var(--t3)" }}>Visible: {(age / 1000).toFixed(1)}s · Watching for cancellation...</div>
                            </div>
                          );
                        })
                      : <div style={{ color: "var(--t3)", textAlign: "center", padding: 28, fontSize: 11, fontFamily: "var(--font-mono)" }}>No active candidates ≥ 5 BTC</div>
                    }
                  </div>
                </div>
              </div>
            );
          })()}
        </section>

        {/* ══════════ ALERTS TAB ══════════ */}
        <section className={`lr-tab-content${activeTab === "alerts" ? " active" : ""}`}>
          <div style={{ maxWidth: 760 }}>
            <div className="lr-card" style={{ marginBottom: 12 }}>
              <div className="lr-card-title">ALERT SETTINGS</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7 }}>
                {[["sweep","Liquidity Sweep (conf ≥55)"],["spoof","Possible Spoofing (prob ≥70)"],["target","Liquidity Target (score ≥75)"],["squeeze","Squeeze Risk (≥75)"]].map(([key, label]) => (
                  <label key={key} className="lr-check-label">
                    <input type="checkbox" checked={alertSettings[key]} onChange={() => setAlertSettings(prev => ({ ...prev, [key]: !prev[key] }))} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div className="lr-card">
              <div className="lr-card-title">ALERT LOG — <span className="lr-cyan">{alertCount}</span> TOTAL</div>
              <div style={{ maxHeight: 600, overflowY: "auto" }}>
                {alerts.length === 0
                  ? <div style={{ color: "var(--t3)", textAlign: "center", padding: 28, fontSize: 11, fontFamily: "var(--font-mono)" }}>No alerts yet — engine is running.</div>
                  : alerts.map((a, i) => (
                    <div key={i} className={`lr-alert-item ${a.type || "info"}`}>
                      <span className="lr-alert-ico">{a.icon || "🔔"}</span>
                      <div style={{ flex: 1 }}>
                        <div className="lr-alert-title">{a.title || "Alert"}</div>
                        <div className="lr-alert-desc">{a.desc || a.message || ""}</div>
                      </div>
                      <span className="lr-alert-time">{a.ts ? ago(Date.now() - a.ts) : "—"}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        </section>

      </div>
      </main>
      </div>
    </div>
  );
}
